import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  Grid,
  IconButton,
  Paper,
  Slide,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Chip,
  Avatar,
  Tooltip,
  useTheme,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  styled,
  tableCellClasses,
} from "@mui/material";
import {
  Download,
  Login as LoginIcon,
  Logout as LogoutIcon,
  Refresh,
  PhotoCamera,
  Replay,
  CheckCircle,
  FlipCameraIos,
  Close as CloseIcon,
  ArrowBack,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { PuffLoader } from "react-spinners";

// services
import { get, post, put } from "../services/apiMethods";
import URLS from "../services/urlConstants";
import WarehouseContext from "../context/WarehouseContext";
import UserContext from "../context/UserContext";
import PageHeader from "../components/PageHeader";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    fontWeight: 500,
    padding: "6px 8px",
    borderBottom: `1px solid ${
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.06)"
        : "rgba(0,0,0,0.04)"
    }`,
  },
  [`&.${tableCellClasses.head}`]: {
    fontSize: 12,
    fontWeight: 700,
    padding: "8px 8px",
    color:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.95)"
        : "rgba(0,0,0,0.87)",
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.12)"
        : "rgba(0,0,0,0.08)",
    borderBottom: `2px solid ${theme.palette.primary.main}`,
  },
}));

const steps = ["Visitor Details", "Capture Photo", "Confirm & Submit"];

const formatDateTime = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

export default function VisitorInfo() {
  const theme = useTheme();
  const { allWarehouses } = useContext(WarehouseContext);
  const { user } = useContext(UserContext);

  // State management
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [visitors, setVisitors] = useState([]);
  const [isReady, setIsReady] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filter states
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Dropdown data
  const [purposes, setPurposes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [formWarehouse, setFormWarehouse] = useState("");

  // Form fields
  const [visitorName, setVisitorName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [purposeId, setPurposeId] = useState("");
  const [hostId, setHostId] = useState("");
  const [visitorPassNumber, setVisitorPassNumber] = useState("");
  const [laptopSerial, setLaptopSerial] = useState("");

  // Camera
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const streamRef = useRef(null);
  const [facingMode, setFacingMode] = useState("user"); // "user" = front, "environment" = back
  const [showCameraDialog, setShowCameraDialog] = useState(false);
  const isFrontCamera = facingMode === "user";

  // Initialize warehouses
  useEffect(() => {
    const wh =
      allWarehouses?.map((s) => ({
        ...s,
        label: s.code,
        value: s._id,
      })) || [];
    setWarehouses(wh);
    if (wh.length > 0) {
      setFormWarehouse(wh[0]._id);
      setSelectedWarehouses([wh[0]._id]);
    }
  }, [allWarehouses]);

  // Load purposes and employees when formWarehouse changes
  useEffect(() => {
    if (formWarehouse) {
      loadPurposes();
      loadEmployees();
      // Reset host selection since the employee list changes per warehouse
      setHostId("");
    }
  }, [formWarehouse]);

  // Load visitor list
  useEffect(() => {
    loadVisitors();
  }, [selectedWarehouses, startDate, endDate]);

  const loadPurposes = async () => {
    try {
      const { data } = await get(URLS.visitorPurpose.list, {
        params: { warehouse: formWarehouse },
      });
      setPurposes(data.result || []);
    } catch (error) {
      console.error(error);
    }
  };

  const loadEmployees = async () => {
    try {
      const { data } = await get(URLS.employee.list);
      const allEmployees = data.result || [];

      // Get current user ID to exclude from host list
      const currentUserId = user?._id;

      // Filter: only employees who have the selected warehouse in their permissions
      // and exclude the currently logged-in user
      const filtered = allEmployees.filter((emp) => {
        // Exclude current logged-in user
        if (currentUserId && emp._id === currentUserId) return false;

        // Only include employees who have the selected warehouse
        if (formWarehouse && emp.warehouses?.length) {
          return emp.warehouses.some(
            (w) => (w._id || w) === formWarehouse
          );
        }
        return false;
      });

      setEmployees(filtered);
    } catch (error) {
      console.error(error);
    }
  };

  const loadVisitors = async () => {
    setIsReady(false);
    try {
      const params = {};
      if (selectedWarehouses.length > 0) {
        params.warehouse = selectedWarehouses.join(",");
      }
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const { data } = await get(URLS.visitorInfo.list, { params });
      setVisitors(data.result || []);
    } catch (error) {
      console.error(error);
    }
    setTimeout(() => setIsReady(true), 300);
  };

  // Camera controls
  const startCamera = async (mode) => {
    const useMode = mode || facingMode;
    try {
      // Stop any existing stream first
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: useMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraActive(true);
    } catch (err) {
      toast.error("Unable to access camera. Please allow camera permissions.");
      console.error(err);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");

    // If front camera, flip the captured image so it's natural (not mirrored)
    if (isFrontCamera) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0);
    ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset transform

    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
    setCapturedPhoto(dataUrl);
    stopCamera();
  };

  const retakePhoto = () => {
    setCapturedPhoto(null);
    setTimeout(() => startCamera(), 200);
  };

  const toggleCamera = () => {
    const newMode = facingMode === "user" ? "environment" : "user";
    setFacingMode(newMode);
    if (cameraActive) {
      startCamera(newMode);
    }
  };

  const openCameraFullscreen = () => {
    setShowCameraDialog(true);
    setCapturedPhoto(null);
    setTimeout(() => startCamera(), 300);
  };

  const closeCameraDialog = () => {
    stopCamera();
    setCapturedPhoto(null);
    setShowCameraDialog(false);
  };

  const confirmPhoto = () => {
    // Photo is already in capturedPhoto state, close dialog and go to step 2
    stopCamera();
    setShowCameraDialog(false);
    setActiveStep(2);
  };

  // Step navigation
  const handleNext = () => {
    if (activeStep === 0) {
      if (!visitorName.trim()) return toast.error("Visitor name is required");
      if (!phoneNumber.trim()) return toast.error("Phone number is required");
      if (!purposeId) return toast.error("Purpose is required");
      if (!hostId) return toast.error("Host is required");
      if (!visitorPassNumber.trim())
        return toast.error("Visitor pass number is required");

      // Open fullscreen camera dialog
      setActiveStep(1);
      openCameraFullscreen();
    }
  };

  const handleBack = () => {
    if (activeStep === 2) {
      // Go back to step 1 means re-open camera
      setActiveStep(1);
      openCameraFullscreen();
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  // Convert base64 data URL to a File object for S3 upload
  const dataURLtoFile = (dataUrl, filename) => {
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("name", visitorName.trim());
      formData.append("phoneNumber", phoneNumber.trim());
      formData.append("purpose", purposeId);
      formData.append("host", hostId);
      formData.append("visitorPassNumber", visitorPassNumber.trim());
      formData.append("laptopSerial", laptopSerial.trim());
      formData.append("warehouse", formWarehouse);

      if (capturedPhoto) {
        const photoFile = dataURLtoFile(
          capturedPhoto,
          `visitor_${Date.now()}.jpg`
        );
        formData.append("photo", photoFile);
      }

      await post(URLS.visitorInfo.create, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      resetForm();
      loadVisitors();
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const resetForm = () => {
    setActiveStep(0);
    setVisitorName("");
    setPhoneNumber("");
    setPurposeId("");
    setHostId("");
    setVisitorPassNumber("");
    setLaptopSerial("");
    setCapturedPhoto(null);
    setShowCameraDialog(false);
    stopCamera();
  };

  const handleCheckOut = async (visitorId) => {
    try {
      await put(URLS.visitorInfo.checkOut + "/" + visitorId);
      loadVisitors();
    } catch (error) {
      console.error(error);
    }
  };

  const handleDownload = async () => {
    try {
      const params = {};
      if (selectedWarehouses.length > 0) {
        params.warehouse = selectedWarehouses.join(",");
      }
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await get(URLS.visitorInfo.download, {
        params,
        responseType: "arraybuffer",
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Visitor_Report_${new Date().toISOString().split("T")[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      toast.error("Failed to download report");
    }
  };

  // Cleanup camera on unmount
  useEffect(() => {
    return () => stopCamera();
  }, []);

  return (
    <Box>
      <PageHeader title="Visitor Information" module="Visitor Info" addButtonDisabled={true} showAdd={false} showAddField={false} />

      {/* ====== CHECK-IN FORM ====== */}
      <Card
        elevation={2}
        sx={{
          mb: 3,
          borderRadius: 3,
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(145deg, #1e1e2e, #252540)"
              : "linear-gradient(145deg, #f8f9ff, #ffffff)",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700} mb={2}>
            <LoginIcon sx={{ mr: 1, verticalAlign: "middle" }} />
            Visitor Check-In
          </Typography>

          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 0: Visitor Details Form */}
          {activeStep === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6} lg={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Warehouse *</InputLabel>
                  <Select
                    value={formWarehouse}
                    label="Warehouse *"
                    onChange={(e) => setFormWarehouse(e.target.value)}
                  >
                    {warehouses.map((w) => (
                      <MenuItem key={w._id} value={w._id}>
                        {w.code || w.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  required
                  fullWidth
                  size="small"
                  label="Visitor Name"
                  value={visitorName}
                  onChange={(e) => setVisitorName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  required
                  fullWidth
                  size="small"
                  label="Phone Number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Purpose</InputLabel>
                  <Select
                    value={purposeId}
                    label="Purpose"
                    onChange={(e) => setPurposeId(e.target.value)}
                  >
                    {purposes
                      .filter((p) => !p.deleted)
                      .map((p) => (
                        <MenuItem key={p._id} value={p._id}>
                          {p.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Host (Employee)</InputLabel>
                  <Select
                    value={hostId}
                    label="Host (Employee)"
                    onChange={(e) => setHostId(e.target.value)}
                  >
                    {employees
                      .filter((e) => !e.deleted)
                      .map((emp) => (
                        <MenuItem key={emp._id} value={emp._id}>
                          {emp.name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  required
                  fullWidth
                  size="small"
                  label="Visitor Pass Number"
                  value={visitorPassNumber}
                  onChange={(e) => setVisitorPassNumber(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Laptop Serial (Optional)"
                  value={laptopSerial}
                  onChange={(e) => setLaptopSerial(e.target.value)}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                lg={3}
                display="flex"
                alignItems="center"
              >
                <Button
                  variant="contained"
                  onClick={handleNext}
                  fullWidth
                  sx={{ height: 40 }}
                >
                  Next
                </Button>
              </Grid>
            </Grid>
          )}

          {/* Step 1: Camera is handled by fullscreen dialog */}
          {activeStep === 1 && !showCameraDialog && (
            <Box display="flex" flexDirection="column" alignItems="center" py={4}>
              {capturedPhoto ? (
                <>
                  <Box
                    sx={{
                      width: 300,
                      borderRadius: 2,
                      overflow: "hidden",
                      border: `3px solid ${theme.palette.success.main}`,
                      mb: 2,
                    }}
                  >
                    <img
                      src={capturedPhoto}
                      alt="Captured"
                      style={{ width: "100%", display: "block" }}
                    />
                  </Box>
                  <Typography variant="body2" color="success.main" mb={2} fontWeight={600}>
                    Photo captured successfully!
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Button variant="outlined" onClick={() => setActiveStep(0)}>
                      Back
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Replay />}
                      onClick={openCameraFullscreen}
                    >
                      Retake
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<CheckCircle />}
                      onClick={() => setActiveStep(2)}
                    >
                      Next
                    </Button>
                  </Stack>
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    Opening camera...
                  </Typography>
                  <Button variant="contained" startIcon={<PhotoCamera />} onClick={openCameraFullscreen}>
                    Open Camera
                  </Button>
                </>
              )}
            </Box>
          )}

          {/* Hidden canvas for photo capture */}
          <canvas ref={canvasRef} style={{ display: "none" }} />

          {/* Step 2: Confirm & Submit */}
          {activeStep === 2 && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.02)",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="primary"
                      fontWeight={700}
                      mb={1}
                    >
                      Visitor Details
                    </Typography>
                    <Stack spacing={1}>
                      <Typography variant="body2">
                        <strong>Name:</strong> {visitorName}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Phone:</strong> {phoneNumber}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Purpose:</strong>{" "}
                        {purposes.find((p) => p._id === purposeId)?.name ||
                          "N/A"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Host:</strong>{" "}
                        {employees.find((e) => e._id === hostId)?.name || "N/A"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Pass No.:</strong> {visitorPassNumber}
                      </Typography>
                      {laptopSerial && (
                        <Typography variant="body2">
                          <strong>Laptop Serial:</strong> {laptopSerial}
                        </Typography>
                      )}
                      <Typography variant="body2">
                        <strong>Warehouse:</strong>{" "}
                        {warehouses.find((w) => w._id === formWarehouse)
                          ?.code || "N/A"}
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  display="flex"
                  justifyContent="center"
                >
                  {capturedPhoto && (
                    <Box
                      sx={{
                        width: 240,
                        borderRadius: 2,
                        overflow: "hidden",
                        border: `2px solid ${theme.palette.primary.main}`,
                      }}
                    >
                      <img
                        src={capturedPhoto}
                        alt="Visitor"
                        style={{ width: "100%", display: "block" }}
                      />
                    </Box>
                  )}
                </Grid>
              </Grid>
              <Stack direction="row" spacing={2} mt={3} justifyContent="center">
                <Button variant="outlined" onClick={handleBack}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleSubmit}
                  disabled={loading}
                  startIcon={<CheckCircle />}
                  size="large"
                >
                  {loading ? "Submitting..." : "Submit & Check In"}
                </Button>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ====== VISITOR LOG LIST ====== */}
      <Card elevation={2} sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            spacing={2}
            mb={2}
          >
            <Typography variant="h6" fontWeight={700}>
              Visitor Log
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Warehouses</InputLabel>
                <Select
                  multiple
                  value={selectedWarehouses}
                  onChange={(e) => setSelectedWarehouses(e.target.value)}
                  input={<OutlinedInput label="Warehouses" />}
                  renderValue={(selected) =>
                    selected
                      .map(
                        (id) =>
                          allWarehouses.find((w) => w._id === id)?.code || id
                      )
                      .join(", ")
                  }
                >
                  {allWarehouses.map((w) => (
                    <MenuItem key={w._id} value={w._id}>
                      <Checkbox
                        checked={selectedWarehouses.includes(w._id)}
                      />
                      <ListItemText primary={w.code || w.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                type="date"
                size="small"
                label="From"
                InputLabelProps={{ shrink: true }}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <TextField
                type="date"
                size="small"
                label="To"
                InputLabelProps={{ shrink: true }}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />

              <Tooltip title="Refresh">
                <IconButton onClick={loadVisitors} color="primary">
                  <Refresh />
                </IconButton>
              </Tooltip>

              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={handleDownload}
                size="small"
              >
                Download
              </Button>
            </Stack>
          </Stack>

          {!isReady ? (
            <Box display="flex" justifyContent="center" py={5}>
              <PuffLoader color={theme.palette.primary.main} size={40} />
            </Box>
          ) : visitors.length === 0 ? (
            <Box textAlign="center" py={5}>
              <Typography color="text.secondary">
                No visitor records found
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <StyledTableCell align="center">#</StyledTableCell>
                      <StyledTableCell align="center">Photo</StyledTableCell>
                      <StyledTableCell align="center">
                        Visitor Name
                      </StyledTableCell>
                      <StyledTableCell align="center">Phone</StyledTableCell>
                      <StyledTableCell align="center">Purpose</StyledTableCell>
                      <StyledTableCell align="center">Host</StyledTableCell>
                      <StyledTableCell align="center">
                        Pass No.
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        Laptop Serial
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        Warehouse
                      </StyledTableCell>
                      <StyledTableCell align="center">In Time</StyledTableCell>
                      <StyledTableCell align="center">Out Time</StyledTableCell>
                      <StyledTableCell align="center">Status</StyledTableCell>
                      <StyledTableCell align="center">Action</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visitors
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((visitor, index) => (
                        <TableRow key={visitor._id} hover>
                          <StyledTableCell align="center">
                            {page * rowsPerPage + index + 1}
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            {visitor.photo ? (
                              <Avatar
                                src={visitor.photo}
                                alt={visitor.name}
                                sx={{ width: 36, height: 36, mx: "auto" }}
                              />
                            ) : (
                              <Avatar
                                sx={{ width: 36, height: 36, mx: "auto" }}
                              >
                                {visitor.name?.charAt(0)?.toUpperCase()}
                              </Avatar>
                            )}
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            {visitor.name}
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            {visitor.phoneNumber}
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            {visitor.purpose?.name || "N/A"}
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            {visitor.host?.name || "N/A"}
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            {visitor.visitorPassNumber}
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            {visitor.laptopSerial || "-"}
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            {visitor.warehouse?.code ||
                              visitor.warehouse?.name ||
                              "N/A"}
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            {formatDateTime(visitor.inTime)}
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            {formatDateTime(visitor.outTime)}
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            {visitor.outTime ? (
                              <Chip
                                label="Checked Out"
                                size="small"
                                color="default"
                                variant="outlined"
                              />
                            ) : (
                              <Chip
                                label="Inside"
                                size="small"
                                color="success"
                                variant="filled"
                              />
                            )}
                          </StyledTableCell>
                          <StyledTableCell align="center">
                            {!visitor.outTime && (
                              <Tooltip title="Check Out">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleCheckOut(visitor._id)}
                                >
                                  <LogoutIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </StyledTableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={visitors.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* ====== FULLSCREEN CAMERA DIALOG ====== */}
      <Dialog
        fullScreen
        open={showCameraDialog}
        onClose={closeCameraDialog}
        TransitionComponent={Slide}
        TransitionProps={{ direction: "up" }}
        PaperProps={{
          sx: {
            backgroundColor: "#000",
            overflow: "hidden",
          },
        }}
      >
        {!capturedPhoto ? (
          /* ---- LIVE CAMERA VIEW ---- */
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#000",
            }}
          >
            {/* Video feed */}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transform: isFrontCamera ? "scaleX(-1)" : "none",
              }}
            />

            {/* Top bar overlay */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.5,
                background:
                  "linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)",
                zIndex: 10,
              }}
            >
              <IconButton onClick={closeCameraDialog} sx={{ color: "#fff" }}>
                <CloseIcon fontSize="large" />
              </IconButton>
              <Typography
                variant="subtitle1"
                sx={{ color: "#fff", fontWeight: 700, letterSpacing: 1 }}
              >
                TAKE VISITOR PHOTO
              </Typography>
              <IconButton onClick={toggleCamera} sx={{ color: "#fff" }}>
                <FlipCameraIos fontSize="large" />
              </IconButton>
            </Box>

            {/* Crosshair / face guide overlay */}
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 220,
                height: 280,
                borderRadius: "50%",
                border: "2px dashed rgba(255,255,255,0.35)",
                pointerEvents: "none",
                zIndex: 5,
              }}
            />

            {/* Bottom bar overlay */}
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pb: 5,
                pt: 3,
                background:
                  "linear-gradient(to top, rgba(0,0,0,0.7), transparent)",
                zIndex: 10,
              }}
            >
              <Stack direction="row" spacing={4} alignItems="center">
                {/* Back button */}
                <IconButton
                  onClick={closeCameraDialog}
                  sx={{
                    color: "#fff",
                    border: "2px solid rgba(255,255,255,0.5)",
                    width: 52,
                    height: 52,
                  }}
                >
                  <ArrowBack />
                </IconButton>

                {/* Capture button */}
                <IconButton
                  onClick={capturePhoto}
                  disabled={!cameraActive}
                  sx={{
                    width: 76,
                    height: 76,
                    border: "4px solid #fff",
                    backgroundColor: "rgba(255,255,255,0.15)",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.3)",
                    },
                    "&:disabled": {
                      border: "4px solid rgba(255,255,255,0.3)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 58,
                      height: 58,
                      borderRadius: "50%",
                      backgroundColor: cameraActive ? "#fff" : "rgba(255,255,255,0.3)",
                      transition: "all 0.2s",
                    }}
                  />
                </IconButton>

                {/* Flip camera button */}
                <IconButton
                  onClick={toggleCamera}
                  sx={{
                    color: "#fff",
                    border: "2px solid rgba(255,255,255,0.5)",
                    width: 52,
                    height: 52,
                  }}
                >
                  <FlipCameraIos />
                </IconButton>
              </Stack>
            </Box>
          </Box>
        ) : (
          /* ---- CAPTURED PHOTO REVIEW ---- */
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#000",
            }}
          >
            {/* Captured image */}
            <img
              src={capturedPhoto}
              alt="Captured Visitor"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />

            {/* Top bar */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
                py: 1.5,
                background:
                  "linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)",
                zIndex: 10,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ color: "#fff", fontWeight: 700, letterSpacing: 1 }}
              >
                REVIEW PHOTO
              </Typography>
            </Box>

            {/* Bottom action bar */}
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pb: 5,
                pt: 3,
                background:
                  "linear-gradient(to top, rgba(0,0,0,0.7), transparent)",
                zIndex: 10,
              }}
            >
              <Stack direction="row" spacing={3} alignItems="center">
                <Button
                  variant="outlined"
                  startIcon={<Replay />}
                  onClick={retakePhoto}
                  sx={{
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.6)",
                    px: 3,
                    py: 1.2,
                    fontSize: "1rem",
                    "&:hover": {
                      borderColor: "#fff",
                      backgroundColor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  Retake
                </Button>
                <Button
                  variant="contained"
                  startIcon={<CheckCircle />}
                  onClick={confirmPhoto}
                  color="success"
                  sx={{
                    px: 4,
                    py: 1.2,
                    fontSize: "1rem",
                    fontWeight: 700,
                  }}
                >
                  Use Photo
                </Button>
              </Stack>
            </Box>
          </Box>
        )}
      </Dialog>
    </Box>
  );
}
